import React, { useState } from "react";
import { Form, Button, Dropdown, Message } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTable } from "../../../../hooks";
import { TABLE_KINDS } from "../../../../utils/constants";
import "./AddEditTableForm.scss";

const KIND_OPTIONS = TABLE_KINDS.map((kind) => ({
  key: kind.value,
  value: kind.value,
  text: kind.text,
  icon: kind.icon,
}));

// Mesa del salón = ambiente + número propio del ambiente (Terraza · Mesa 1); los
// números se repiten entre ambientes y el sistema sugiere el primero libre. El
// número interno único (va en el QR) lo pone el sistema. Las mesas virtuales
// (venta rápida, WhatsApp, delivery) no tienen ambiente y llevan nombre propio.
export function AddEditTableForm(props) {
  const { onClose, onRefetch, table, areas = [] } = props;
  const { addTable, updateTable } = useTable();
  const [serverError, setServerError] = useState("");

  const formik = useFormik({
    initialValues: initialValues(table, areas),
    validationSchema: Yup.object(validationSchema()),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      setServerError("");
      const data =
        formValue.kind === "SALON"
          ? {
              kind: "SALON",
              area: formValue.area,
              area_number: Number(formValue.area_number),
              label: formValue.label,
            }
          : { kind: formValue.kind, number: Number(formValue.number), label: formValue.label };
      try {
        if (table) await updateTable(table.id, data);
        else await addTable(data);
      } catch (error) {
        setServerError(error.message);
        return;
      }
      onRefetch();
      onClose();
    },
  });

  const isSalon = formik.values.kind === "SALON";
  const area = areas.find((a) => a.id === formik.values.area);
  const movingArea = table && table.area && formik.values.area !== table.area;

  const onAreaChange = (value) => {
    formik.setFieldValue("area", value);
    // Al cambiar de ambiente se sugiere su primer número libre (al volver al
    // ambiente original, la mesa conserva su número).
    const target = areas.find((a) => a.id === value);
    if (table && value === table.area) formik.setFieldValue("area_number", table.area_number);
    else if (target) formik.setFieldValue("area_number", target.next_number);
  };

  return (
    <Form className="add-edit-table-form" onSubmit={formik.handleSubmit}>
      <label className="add-edit-table-form__label">Tipo</label>
      <Dropdown
        fluid
        selection
        options={KIND_OPTIONS}
        value={formik.values.kind}
        onChange={(_, data) => formik.setFieldValue("kind", data.value)}
      />
      {!isSalon && (
        <p className="add-edit-table-form__hint">
          Mesa virtual: venta rápida (pide en el mostrador para llevar), WhatsApp (llegó
          por mensaje) o delivery (se envía). Usa el mismo
          flujo que una mesa del salón (añadir pedido, cocina, cobrar) y sus
          ventas salen en los reportes por canal.
        </p>
      )}

      {isSalon && areas.length > 0 && (
        <>
          <label className="add-edit-table-form__label">Ambiente</label>
          <Dropdown
            fluid
            selection
            options={areas.map((a) => ({ key: a.id, value: a.id, text: a.name }))}
            value={formik.values.area}
            onChange={(_, data) => onAreaChange(data.value)}
          />
        </>
      )}

      <label className="add-edit-table-form__label">
        {isSalon ? `Número de mesa${area ? ` en ${area.name}` : ""}` : "Número interno"}
      </label>
      {isSalon ? (
        <Form.Input
          name="area_number"
          type="number"
          min="1"
          placeholder="Ej. 1"
          value={formik.values.area_number}
          onChange={formik.handleChange}
          error={formik.errors.area_number}
        />
      ) : (
        <Form.Input
          name="number"
          type="number"
          placeholder="Número interno único, ej. 101 (no se muestra)"
          value={formik.values.number}
          onChange={formik.handleChange}
          error={formik.errors.number}
        />
      )}
      {isSalon && (
        <p className="add-edit-table-form__hint">
          Cada ambiente numera sus mesas desde 1. Si quitas una mesa, las demás conservan
          su número.
          {movingArea && " Al moverla de ambiente, reimprime el número de la mesa: el QR sigue funcionando."}
        </p>
      )}

      <label className="add-edit-table-form__label">
        Nombre visible {isSalon && "(opcional)"}
      </label>
      <Form.Input
        name="label"
        placeholder={
          isSalon
            ? 'Vacío = "Mesa N"'
            : formik.values.kind === "TAKEAWAY"
            ? "Ej. Venta rápida 1"
            : formik.values.kind === "DELIVERY"
            ? "Ej. Delivery 1"
            : "Ej. WhatsApp 1"
        }
        maxLength={30}
        value={formik.values.label}
        onChange={formik.handleChange}
        error={formik.errors.label}
      />

      {serverError && <Message negative content={serverError} />}

      <Button
        type="submit"
        primary
        fluid
        content={table ? "Actualizar" : "Crear"}
      />
    </Form>
  );
}

function initialValues(data, areas) {
  const firstArea = areas[0];
  return {
    kind: data?.kind || "SALON",
    number: data?.kind && data.kind !== "SALON" ? data.number : "",
    area: data?.area || firstArea?.id || null,
    area_number: data?.area_number || (data ? "" : firstArea?.next_number || 1),
    label: data?.label || "",
  };
}

function validationSchema() {
  return {
    // Cada número solo se valida en su tipo de mesa (el otro campo está oculto y vacío).
    area_number: Yup.mixed().when("kind", {
      is: "SALON",
      then: Yup.number().typeError("Escribe el número").min(1, "Desde 1").required("Escribe el número"),
      otherwise: Yup.mixed(),
    }),
    number: Yup.mixed().when("kind", {
      is: (kind) => kind && kind !== "SALON",
      then: Yup.number().typeError("Escribe un número, ej. 101").required("Escribe un número, ej. 101"),
      otherwise: Yup.mixed(),
    }),
    // Una mesa virtual necesita nombre: si no, se vería como "Mesa 101".
    label: Yup.string().when("kind", {
      is: (kind) => kind && kind !== "SALON",
      then: Yup.string().required("Ponle un nombre, ej. Venta rápida 1"),
    }),
  };
}
